import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { IconInfoCircle } from '@tabler/icons-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { ProductSearchRow, UpliftRow } from '@/types/promoAttribution';
import { formatPromoNumber } from '@/utils/promoFormatNumber';
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
} from '@/components/ui/tooltip';

function ColHeader({ label, tip }: { label: string; tip: string }) {
  return (
    <span className="inline-flex items-center gap-1">
      {label}
      <Tooltip>
        <TooltipTrigger render={<span className="inline-flex cursor-default outline-none" />}>
          <IconInfoCircle size={13} className="text-muted-foreground/60 shrink-0 cursor-default" />
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-60 text-center leading-snug">
          {tip}
        </TooltipContent>
      </Tooltip>
    </span>
  );
}

export function PromoAttributionProductsPage() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [results, setResults] = useState<ProductSearchRow[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [upliftRows, setUpliftRows] = useState<UpliftRow[]>([]);
  const [upliftLoading, setUpliftLoading] = useState(true);

  useEffect(() => {
    const t = setTimeout(() => setDebouncedQuery(query.trim()), 400);
    return () => clearTimeout(t);
  }, [query]);

  const runProductSearch = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearchLoading(true);
    try {
      const res = await api.get(`/spar-mobile/promo-attribution/products?q=${encodeURIComponent(q)}`);
      setResults(res.data.data ?? []);
    } finally {
      setSearchLoading(false);
    }
  }, []);

  useEffect(() => { runProductSearch(debouncedQuery); }, [debouncedQuery, runProductSearch]);

  useEffect(() => {
    // The uplift leaderboard is only rendered when there is no active search
    // (see the `debouncedQuery.length === 0` guard around the table below),
    // so skip the fetch and reset state while the user is searching.
    if (debouncedQuery.length > 0) {
      setUpliftRows([]);
      setUpliftLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      setUpliftLoading(true);
      try {
        const res = await api.get('/spar-mobile/promo-attribution/top-uplift?limit=20');
        if (!cancelled) setUpliftRows(res.data.data ?? []);
      } finally {
        if (!cancelled) setUpliftLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [debouncedQuery]);

  return (
    <div className="space-y-6">
      <div className="relative">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search GTIN or product name…"
          autoFocus
          className="w-full h-10 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
        />
        {(searchLoading || upliftLoading || query.trim() !== debouncedQuery) && (
          <span className="absolute right-3 top-2.5 text-xs text-muted-foreground">Updating…</span>
        )}
      </div>

      {debouncedQuery.length === 0 && (
      <div className="rounded-lg border bg-card overflow-hidden">
        <div className="p-4 pb-2">
          <p className="text-base font-semibold">Top products by Promo Data uplift</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Products with ≥3 days in each state. Uplift = avg daily units with Promo Data vs without.
          </p>
        </div>
        {upliftRows.length > 0 ? (
          <table className="w-full text-sm">
            <thead className="bg-muted/40">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="px-4 py-2 font-medium w-8">#</th>
                <th className="px-4 py-2 font-medium">GTIN</th>
                <th className="px-4 py-2 font-medium">Product</th>
                <th className="px-4 py-2 font-medium text-right">
                  <ColHeader
                    label="Avg units (w/ Promo Data)"
                    tip="Average daily units sold on days where this product's winning state was Promo Data or Both (discounted + attributed)."
                  />
                </th>
                <th className="px-4 py-2 font-medium text-right">
                  <ColHeader
                    label="Avg units (w/o Promo Data)"
                    tip="Average daily units sold on days where this product's winning state was Standard Price or Promotion (no promo-data attribution)."
                  />
                </th>
                <th className="px-4 py-2 font-medium text-right">
                  <ColHeader
                    label="Uplift %"
                    tip="(Avg w/ Promo Data − Avg w/o) ÷ Avg w/o × 100. Only products with ≥3 days in each bucket are included."
                  />
                </th>
                <th className="px-4 py-2 font-medium text-right">Days w/ Promo</th>
                <th className="px-4 py-2 font-medium" />
              </tr>
            </thead>
            <tbody>
              {upliftRows.map((row, i) => (
                <tr key={row.gtin} className="border-t">
                  <td className="px-4 py-2 text-muted-foreground">{i + 1}</td>
                  <td className="px-4 py-2 font-mono text-sm">{row.gtin}</td>
                  <td className="px-4 py-2 text-sm max-w-[180px] truncate" title={row.description ?? undefined}>
                    {row.description ?? <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="px-4 py-2 text-right">{formatPromoNumber(row.avg_units_promo_data, 1)}</td>
                  <td className="px-4 py-2 text-right">{formatPromoNumber(row.avg_units_no_promo_data, 1)}</td>
                  <td className={cn('px-4 py-2 text-right font-semibold tabular-nums', row.uplift_pct >= 0 ? 'text-emerald-600' : 'text-red-500')}>
                    {row.uplift_pct >= 0 ? '+' : ''}{formatPromoNumber(row.uplift_pct, 1)}%
                  </td>
                  <td className="px-4 py-2 text-right text-muted-foreground">{formatPromoNumber(row.days_with_promo_data)}</td>
                  <td className="px-4 py-2">
                    <Link
                      to={`/spar-mobile/promo-attribution/products/${row.gtin}`}
                      className="text-xs text-blue-600 hover:underline font-medium"
                    >
                      View →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <div className="h-24 flex items-center justify-center text-muted-foreground text-sm px-4 text-center">
            {upliftLoading ? 'Loading…' : 'No uplift data for this window.'}
          </div>
        )}
      </div>
      )}

      {results.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold tracking-tight mb-1">All matching products</h2>
          <p className="text-sm text-muted-foreground mb-4">
            Every product matching your search that has had Promo Data rules applied — including those with too few days to qualify for the uplift leaderboard above.
          </p>
          <div className="rounded-lg border bg-card overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/40">
                <tr className="text-left text-xs text-muted-foreground">
                  <th className="px-4 py-2 font-medium">GTIN</th>
                  <th className="px-4 py-2 font-medium">Product</th>
                  <th className="px-4 py-2 font-medium text-right">
                    <ColHeader
                      label="Avg units (w/ Promo Data)"
                      tip="Average daily units sold on days where this product's winning state was Promo Data or Both (discounted + attributed)."
                    />
                  </th>
                  <th className="px-4 py-2 font-medium text-right">
                    <ColHeader
                      label="Avg units (w/o Promo Data)"
                      tip="Average daily units sold on days where this product's winning state was Standard Price or Promotion (no promo-data attribution)."
                    />
                  </th>
                  <th className="px-4 py-2 font-medium text-right">
                    <ColHeader
                      label="Uplift %"
                      tip="(Avg w/ Promo Data − Avg w/o) ÷ Avg w/o × 100. Shown only when the product has at least one day in each bucket."
                    />
                  </th>
                  <th className="px-4 py-2 font-medium text-right">Days w/ Promo</th>
                  <th className="px-4 py-2 font-medium text-right">Days w/o Promo</th>
                  <th className="px-4 py-2 font-medium" />
                </tr>
              </thead>
              <tbody>
                {results.map((row) => (
                  <tr key={row.gtin} className="border-t">
                    <td className="px-4 py-2 font-mono text-sm">{row.gtin}</td>
                    <td className="px-4 py-2 text-sm max-w-[180px] truncate" title={row.description ?? undefined}>
                      {row.description ?? <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {row.avg_units_promo_data != null
                        ? formatPromoNumber(row.avg_units_promo_data, 1)
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="px-4 py-2 text-right">
                      {row.avg_units_no_promo_data != null
                        ? formatPromoNumber(row.avg_units_no_promo_data, 1)
                        : <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className={cn(
                      'px-4 py-2 text-right font-semibold tabular-nums',
                      row.uplift_pct == null ? 'text-muted-foreground' : row.uplift_pct >= 0 ? 'text-emerald-600' : 'text-red-500',
                    )}>
                      {row.uplift_pct == null
                        ? '—'
                        : `${row.uplift_pct >= 0 ? '+' : ''}${formatPromoNumber(row.uplift_pct, 1)}%`}
                    </td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{formatPromoNumber(row.days_with_promo_data)}</td>
                    <td className="px-4 py-2 text-right text-muted-foreground">{formatPromoNumber(row.days_without_promo_data)}</td>
                    <td className="px-4 py-2">
                      <Link
                        to={`/spar-mobile/promo-attribution/products/${row.gtin}`}
                        className="text-xs text-blue-600 hover:underline font-medium"
                      >
                        View →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {debouncedQuery.length >= 2 && !searchLoading && results.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-4">
          No products found matching &ldquo;{debouncedQuery}&rdquo;.
        </p>
      )}

      {query.trim().length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-2">
          Type at least two characters to see all matching products below the uplift table.
        </p>
      )}
    </div>
  );
}
